import type { Workout } from "./types";

export const WORKOUTS: Workout[] = [
  // Level 1
  {
    slug: "wake-up-your-feet",
    title: "Wake Up Your Feet",
    level: 1,
    minutes: 12,
    tags: ["Toe Splay", "Daily Routine"],
    description:
      "A gentle daily starter that reintroduces movement to toes and arches that have spent years locked in shoes.",
    exercises: [
      { name: "Toe yoga (spread & lift)", prescription: "3 x 10 reps each foot" },
      { name: "Toe splay holds", prescription: "3 x 20s each foot" },
      { name: "Toe curls on a towel", prescription: "2 x 10 reps each foot" },
      { name: "Ankle circles", prescription: "2 x 10 each direction, each foot" },
      { name: "Short foot holds (seated)", prescription: "3 x 15s each foot" },
    ],
  },
  {
    slug: "first-steps-balance",
    title: "First Steps to Balance",
    level: 1,
    minutes: 10,
    tags: ["Single-leg Balance", "Warm Up"],
    description: "Builds the base level of single-leg stability everything else in the Academy depends on.",
    exercises: [
      { name: "Single-leg stance (hand support)", prescription: "3 x 20s each leg" },
      { name: "Single-leg stance (no support)", prescription: "3 x 15s each leg" },
      { name: "Weight shifts side to side", prescription: "2 x 10 each side" },
      { name: "Heel-to-toe stance hold", prescription: "2 x 20s" },
    ],
  },
  {
    slug: "ankle-reset",
    title: "Ankle Reset",
    level: 1,
    minutes: 12,
    tags: ["Ankle Mobility", "Daily Routine"],
    description: "Restores basic forward ankle range lost from years of raised heels and rigid soles.",
    exercises: [
      { name: "Knee-to-wall ankle rocks", prescription: "3 x 10 reps each foot" },
      { name: "Ankle circles", prescription: "2 x 10 each direction" },
      { name: "Heel walks", prescription: "2 x 20m" },
      { name: "Calf stretch against wall", prescription: "2 x 30s each side" },
    ],
  },
  {
    slug: "arch-foundations",
    title: "Arch Foundations",
    level: 1,
    minutes: 15,
    tags: ["Pronation", "Toe Splay"],
    description: "Combines toe splay and short-foot drills to teach the arch to support itself actively.",
    exercises: [
      { name: "Toe splay holds", prescription: "3 x 20s each foot" },
      { name: "Short foot holds (standing)", prescription: "3 x 15s each foot" },
      { name: "Towel scrunches", prescription: "2 x 10 reps each foot" },
      { name: "Marble pickups (or pretend pickups)", prescription: "2 x 10 reps each foot" },
    ],
  },
  {
    slug: "glute-switch-on",
    title: "Glute Switch-On",
    level: 1,
    minutes: 12,
    tags: ["Glute Activation", "Daily Routine"],
    description: "A short activation circuit for glutes that have gone dormant from long hours of sitting.",
    exercises: [
      { name: "Glute bridge", prescription: "3 x 12 reps" },
      { name: "Clamshells", prescription: "2 x 12 each side" },
      { name: "Fire hydrants", prescription: "2 x 10 each side" },
      { name: "Standing hip abduction hold", prescription: "2 x 15s each side" },
    ],
  },
  {
    slug: "bridge-builder",
    title: "Bridge Builder",
    level: 1,
    minutes: 15,
    tags: ["Glute Activation", "Glute Strengthening"],
    description: "Progresses the basic glute bridge toward the marching and single-leg variations used later.",
    exercises: [
      { name: "Glute bridge", prescription: "3 x 15 reps" },
      { name: "Glute bridge march", prescription: "2 x 8 each side" },
      { name: "Clamshells", prescription: "2 x 12 each side" },
      { name: "Glute bridge hold", prescription: "2 x 30s" },
    ],
  },
  {
    slug: "core-basics",
    title: "Core Basics",
    level: 1,
    minutes: 12,
    tags: ["Core", "Daily Routine"],
    description: "Foundational anti-rotation and anti-extension core work that supports every other pattern.",
    exercises: [
      { name: "Dead bug", prescription: "2 x 10 each side" },
      { name: "Bird dog", prescription: "2 x 10 each side" },
      { name: "Front plank (knees or toes)", prescription: "3 x 20s" },
      { name: "Side plank (knees)", prescription: "2 x 15s each side" },
    ],
  },
  {
    slug: "hip-opener",
    title: "Gentle Hip Opener",
    level: 1,
    minutes: 15,
    tags: ["Hip Mobility", "Warm Up"],
    description: "Loosens tight hips from prolonged sitting and prepares them for loaded hinge and squat work.",
    exercises: [
      { name: "Hip circles (standing)", prescription: "2 x 8 each direction, each leg" },
      { name: "Kneeling hip flexor stretch", prescription: "2 x 30s each side" },
      { name: "90/90 hip switches", prescription: "2 x 8 each side" },
      { name: "Standing figure-4 stretch", prescription: "2 x 20s each side" },
    ],
  },
  {
    slug: "morning-mobility",
    title: "10-Minute Morning Mobility",
    level: 1,
    minutes: 10,
    tags: ["Daily Routine", "Warm Up", "Shoulder Mobility"],
    description: "A quick full-body wake-up routine designed to be done first thing, every day.",
    exercises: [
      { name: "Wall shoulder slides", prescription: "2 x 10 reps" },
      { name: "Thoracic rotations", prescription: "2 x 8 each side" },
      { name: "Ankle circles", prescription: "1 x 10 each direction, each foot" },
      { name: "Hip circles", prescription: "1 x 8 each direction, each leg" },
      { name: "Cat-cow", prescription: "1 x 10 reps" },
    ],
  },
  {
    slug: "squat-prep",
    title: "Squat Prep",
    level: 1,
    minutes: 15,
    tags: ["Squatting", "Ankle Mobility", "Hip Mobility"],
    description: "Opens up the ankles and hips needed to sit into a comfortable, controlled squat position.",
    exercises: [
      { name: "Knee-to-wall ankle rocks", prescription: "2 x 10 each foot" },
      { name: "90/90 hip switches", prescription: "2 x 8 each side" },
      { name: "Assisted deep squat hold (hands on support)", prescription: "3 x 20s" },
      { name: "Cossack squat (partial range)", prescription: "2 x 8 each side" },
    ],
  },

  // Level 2
  {
    slug: "toe-control",
    title: "Toe Control Circuit",
    level: 2,
    minutes: 15,
    tags: ["Toe Splay", "Pronation"],
    description: "Sharpens fine motor control of the toes and arch beyond the basics.",
    exercises: [
      { name: "Individual toe lifts (big toe only, little toes only)", prescription: "3 x 8 each foot" },
      { name: "Towel scrunches", prescription: "3 x 12 reps each foot" },
      { name: "Short foot holds (single-leg)", prescription: "3 x 20s each foot" },
      { name: "Toe splay with resistance (fingers between toes)", prescription: "2 x 10 reps" },
    ],
  },
  {
    slug: "single-leg-strong",
    title: "Single-Leg Strong",
    level: 2,
    minutes: 18,
    tags: ["Single-leg Balance", "Glute Strengthening"],
    description: "Layers glute strength onto single-leg balance so stability holds up under real movement.",
    exercises: [
      { name: "Single-leg stance, eyes closed", prescription: "3 x 20s each leg" },
      { name: "Single-leg bridge", prescription: "3 x 10 each side" },
      { name: "Step-downs (low step)", prescription: "2 x 8 each side" },
      { name: "Single-leg reach (hip hinge)", prescription: "2 x 8 each side" },
    ],
  },
  {
    slug: "calf-capacity",
    title: "Calf Capacity",
    level: 2,
    minutes: 15,
    tags: ["Supination", "Ankle Mobility"],
    description: "Builds calf raise volume and control to raise your push-off ceiling.",
    exercises: [
      { name: "Straight-knee calf raises", prescription: "3 x 15 reps" },
      { name: "Bent-knee calf raises", prescription: "3 x 15 reps" },
      { name: "Single-leg calf raise (assisted)", prescription: "2 x 8 each side" },
      { name: "Knee-to-wall ankle rocks", prescription: "2 x 10 each foot" },
    ],
  },
  {
    slug: "pronation-control",
    title: "Pronation Control",
    level: 2,
    minutes: 15,
    tags: ["Pronation", "Single-leg Balance"],
    description: "Trains the arch to control rolling-in forces under single-leg load instead of collapsing.",
    exercises: [
      { name: "Short foot holds (single-leg)", prescription: "3 x 20s each foot" },
      { name: "Single-leg balance with arch hold", prescription: "3 x 20s each leg" },
      { name: "Slow single-leg squat to 30 degrees, arch held", prescription: "2 x 8 each side" },
      { name: "Lateral step-overs", prescription: "2 x 10 each side" },
    ],
  },
  {
    slug: "glute-strength-1",
    title: "Glute Strength Builder",
    level: 2,
    minutes: 18,
    tags: ["Glute Strengthening", "Core"],
    description: "Progresses glute work into single-leg territory while integrating core stability.",
    exercises: [
      { name: "Single-leg bridge", prescription: "3 x 12 each side" },
      { name: "Bird dog", prescription: "2 x 10 each side" },
      { name: "Side plank", prescription: "2 x 25s each side" },
      { name: "Clamshells", prescription: "2 x 15 each side" },
    ],
  },
  {
    slug: "side-body",
    title: "Side-Body Strength",
    level: 2,
    minutes: 15,
    tags: ["Core", "Glute Activation"],
    description: "Targets the often-neglected glute medius and lateral core chain.",
    exercises: [
      { name: "Side plank with top-leg raise", prescription: "3 x 8 each side" },
      { name: "Standing hip abduction hold", prescription: "2 x 20s each side" },
      { name: "Monster walk (hip abduction steps)", prescription: "2 x 10 steps each direction" },
      { name: "Clamshells", prescription: "2 x 15 each side" },
    ],
  },
  {
    slug: "hinge-patterns",
    title: "Hinge Patterns",
    level: 2,
    minutes: 18,
    tags: ["Hip Mobility", "Glute Strengthening"],
    description: "Builds the hip hinge pattern that underlies deadlifts, picking things up, and running stride.",
    exercises: [
      { name: "Two-leg hip hinge (bodyweight)", prescription: "3 x 12 reps" },
      { name: "Single-leg hinge, hand support", prescription: "3 x 8 each side" },
      { name: "Glute bridge hold", prescription: "2 x 30s" },
      { name: "Standing figure-4 stretch", prescription: "2 x 20s each side" },
    ],
  },
  {
    slug: "runners-reset",
    title: "Runner's Reset",
    level: 2,
    minutes: 15,
    tags: ["Running", "Ankle Mobility", "Warm Up"],
    description: "A pre- or post-run mobility and activation circuit targeting common running weak links.",
    exercises: [
      { name: "Knee-to-wall ankle rocks", prescription: "2 x 10 each foot" },
      { name: "A-skips (in place)", prescription: "2 x 20s" },
      { name: "Single-leg calf raise", prescription: "2 x 12 each side" },
      { name: "Glute bridge march", prescription: "2 x 10 each side" },
    ],
  },
  {
    slug: "deep-squat-flow",
    title: "Deep Squat Flow",
    level: 2,
    minutes: 18,
    tags: ["Squatting", "Hip Mobility"],
    description: "Builds comfort and control in the bottom of a deep squat through active holds and rocking.",
    exercises: [
      { name: "Deep squat hold with rocking", prescription: "3 x 30s" },
      { name: "Cossack squats", prescription: "2 x 8 each side" },
      { name: "Split squats", prescription: "2 x 10 each side" },
      { name: "90/90 hip switches", prescription: "2 x 8 each side" },
    ],
  },
  {
    slug: "desk-undo",
    title: "The Desk Undo",
    level: 2,
    minutes: 12,
    tags: ["Daily Routine", "Shoulder Mobility", "Hip Mobility"],
    description: "Counters the effects of a day spent sitting and hunched over a screen.",
    exercises: [
      { name: "Wall shoulder slides", prescription: "2 x 12 reps" },
      { name: "Thoracic rotations", prescription: "2 x 10 each side" },
      { name: "Kneeling hip flexor stretch", prescription: "2 x 30s each side" },
      { name: "Glute bridge", prescription: "2 x 12 reps" },
    ],
  },

  // Level 3
  {
    slug: "barefoot-athlete",
    title: "Barefoot Athlete Circuit",
    level: 3,
    minutes: 20,
    tags: ["Toe Splay", "Supination", "Single-leg Balance"],
    description: "An advanced foot circuit combining splay, push-off strength, and balance under fatigue.",
    exercises: [
      { name: "Toe splay with resistance", prescription: "3 x 12 reps" },
      { name: "Single-leg calf raise", prescription: "3 x 15 each side" },
      { name: "Single-leg balance, eyes closed, on foam", prescription: "3 x 20s each leg" },
      { name: "Short foot single-leg squat", prescription: "2 x 8 each side" },
    ],
  },
  {
    slug: "single-leg-power",
    title: "Single-Leg Power",
    level: 3,
    minutes: 20,
    tags: ["Glute Strengthening", "Single-leg Balance", "Running"],
    description: "Develops explosive single-leg strength and control for running and change of direction.",
    exercises: [
      { name: "Single-leg bridge with pause", prescription: "3 x 12 each side" },
      { name: "Step-downs (higher step)", prescription: "3 x 10 each side" },
      { name: "Single-leg RDL", prescription: "3 x 8 each side" },
      { name: "Lateral bounds (controlled)", prescription: "2 x 8 each side" },
    ],
  },
  {
    slug: "spring-loaded-calves",
    title: "Spring-Loaded Calves",
    level: 3,
    minutes: 18,
    tags: ["Supination", "Running"],
    description: "High-volume calf and ankle work to build the elastic push-off runners depend on.",
    exercises: [
      { name: "Single-leg calf raise", prescription: "3 x 20 each side" },
      { name: "Pogo hops (small, controlled)", prescription: "3 x 15" },
      { name: "Bent-knee calf raise", prescription: "2 x 15 reps" },
      { name: "A-skips", prescription: "2 x 20s" },
    ],
  },
  {
    slug: "glute-power",
    title: "Glute Power",
    level: 3,
    minutes: 20,
    tags: ["Glute Strengthening", "Core"],
    description: "A demanding glute session combining strength, single-leg control, and core integration.",
    exercises: [
      { name: "Single-leg bridge with pause", prescription: "3 x 12 each side" },
      { name: "Step-downs (higher step)", prescription: "3 x 10 each side" },
      { name: "Side plank with top-leg raise", prescription: "3 x 10 each side" },
      { name: "Bird dog with pause", prescription: "2 x 10 each side" },
    ],
  },
  {
    slug: "full-foundation",
    title: "Full Foundation",
    level: 3,
    minutes: 20,
    tags: ["Daily Routine", "Core", "Squatting"],
    description: "A complete foundation session touching feet, glutes, core, and squat pattern in one workout.",
    exercises: [
      { name: "Short foot single-leg squat", prescription: "3 x 8 each side" },
      { name: "Single-leg bridge", prescription: "3 x 12 each side" },
      { name: "Front plank", prescription: "3 x 40s" },
      { name: "Deep squat hold", prescription: "2 x 45s" },
    ],
  },
  {
    slug: "runners-strength",
    title: "Runner's Strength",
    level: 3,
    minutes: 20,
    tags: ["Running", "Glute Strengthening", "Ankle Mobility"],
    description: "Strength work specifically chosen to shore up the weak links most common in runners.",
    exercises: [
      { name: "Single-leg RDL", prescription: "3 x 10 each side" },
      { name: "Single-leg calf raise", prescription: "3 x 15 each side" },
      { name: "Step-downs", prescription: "3 x 10 each side" },
      { name: "Knee-to-wall ankle rocks", prescription: "2 x 10 each foot" },
    ],
  },
  {
    slug: "control-under-load",
    title: "Control Under Load",
    level: 3,
    minutes: 18,
    tags: ["Pronation", "Squatting", "Core"],
    description: "Tests arch and core control under slower, more demanding single-leg squat patterns.",
    exercises: [
      { name: "Short foot single-leg squat", prescription: "3 x 10 each side" },
      { name: "Cossack squats", prescription: "3 x 10 each side" },
      { name: "Dead bug", prescription: "2 x 12 each side" },
      { name: "Split squats", prescription: "2 x 12 each side" },
    ],
  },
];
